import RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type SkuModel from "volta/models/sku";
import type Owner from "@ember/owner";

export type TSkuDetailModalTab =
  | 'settings'
  | 'planning'
  | 'execution'
  | 'stock'
  | 'transactions'
  | 'bom'
  | 'forecasts';

export type TSkuDetailModalBomTab = 'descendants' | 'parents' | 'finishedProducts' | 'operations';

interface IState {
  visible: boolean;
  tab?: TSkuDetailModalTab;
  bomTab?: TSkuDetailModalBomTab;
  skuId?: string;
  partCode?: string;
  skuDescription?: string;
  acqCode?: string;
  sku?: any;
  options: object;
}

const ZERO_STATE: IState = {
  visible: false,
  tab: 'settings',
  sku: undefined,
  skuId: undefined,
  partCode: undefined,
  skuDescription: undefined,
  acqCode: undefined,
  options: {}
};

export default class SkuModalService extends Service {
  @service router!: RouterService;

  @tracked state: IState;
  @tracked unsavedSkuEdits: boolean = false;

  get isShowing() {
    return this.state.visible;
  }

  constructor(owner: Owner) {
    super(owner);
    this.state = ZERO_STATE;
  }

  show(
    tab?: TSkuDetailModalTab,
    bomTab?: TSkuDetailModalBomTab,
    skuId?: string,
    partCode?: string,
    skuDescription?: string,
    acqCode?: string,
    options: object = {}
  ) {
    const { tab: qsTab, bomTab: qsBomTab } = this.router.currentRoute?.queryParams ?? {};
    this.state = {
      visible: true,
      tab: tab ?? (qsTab as TSkuDetailModalTab),
      bomTab: bomTab ?? (qsBomTab as TSkuDetailModalBomTab),
      skuId,
      partCode,
      skuDescription,
      acqCode,
      sku: undefined,
      options
    };
  }

  showWithSku(
    tab: TSkuDetailModalTab,
    bomTab?: TSkuDetailModalBomTab,
    sku?: Partial<SkuModel>,
    options: object = {}
  ) {
    const { id, partCode, skuDescription, acqCode } = sku ?? {};
    const { tab: qsTab, bomTab: qsBomTab } = this.router.currentRoute?.queryParams ?? {};

    this.state = {
      visible: true,
      tab: tab ?? (qsTab as TSkuDetailModalTab),
      bomTab: bomTab ?? (qsBomTab as TSkuDetailModalBomTab),
      skuId: id!,
      partCode,
      skuDescription,
      acqCode,
      sku,
      options
    };
  }

  hide() {
    this.state = ZERO_STATE;
  }

  switchTab = (tab: TSkuDetailModalTab, bomTab?: TSkuDetailModalBomTab) => {
    if (tab) {
      this.state = { ...this.state, tab, bomTab };
    }
  };
}

declare module '@ember/service' {
  interface Registry {
    'sku-modal-service': SkuModalService;
  }
}
